import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import './Cms.css';
import { AuthContext } from '../../api/authContext';
import { AdminCategory, AdminItem, ApiError, createCategory, fetchAdminCatalog, reorderCategories } from '../../api/client';
import { Button, Card, CardTitle, EmptyState, Field, Input, Meta, Notice, SectionHeader, Stack } from '../../ui';
import type { Preview } from '../group/TillPreview';
import CategoryCard from './CategoryCard';
import { savesInFlight, trackSave } from './cmsActivity';
import {
  clearDraft,
  hasStoredDrafts,
  isNewCategoryDraft,
  newCategoryDraftDirty,
  newCategoryDraftKey,
  readDraft,
  warnBeforeUnload,
  writeDraft,
} from './cmsDrafts';

type CmsProps = {
  /** Called when the session is no longer valid, so App can drop the session. */
  onUnauthorized: () => void;
  /** Publish the category being edited so the page can preview it. */
  onPreview?: (preview: Preview | null) => void;
};

/**
 * Which editor is open.
 *
 * The category, an item and the new-item form are mutually exclusive, so one
 * typed slot holds them instead of three ids that had to be cleared by hand.
 */
type EditTarget =
  { kind: 'category'; categoryId: number } | { kind: 'item'; itemId: number } | { kind: 'newItem'; categoryId: number } | null;

/** Categories start collapsed: the catalogue reads as an index first. */
const STARTS_EXPANDED = false;

/** How many items carry an uploaded asset, for the footer summary. */
function totalAssets(categories: AdminCategory[]): number {
  return categories.reduce((count, category) => count + category.items.filter((item) => item.image_id !== null).length, 0);
}

function plural(count: number, one: string, many: string): string {
  return count + ' ' + (count === 1 ? one : many);
}

function summaryLine(categories: AdminCategory[]): string {
  const items = categories.reduce((count, category) => count + category.items.length, 0);
  return [
    plural(categories.length, 'category', 'categories'),
    plural(items, 'item', 'items'),
    plural(totalAssets(categories), 'uploaded image', 'uploaded images'),
  ].join(' · ');
}

/** A category as the till would draw it, for the page's preview rail. */
function categoryPreview(category: AdminCategory): Preview {
  return {
    kind: 'category',
    name: category.name,
    palette2: category.palette2,
    palette3: category.palette3,
    items: category.items.map((item) => ({
      id: item.id,
      title: item.title,
      priceCents: item.price_cents,
      img: item.image_url ?? item.img,
    })),
  };
}

/** Surface a failure, or hand an expired session back to App to log out. */
function report(error: unknown, onUnauthorized: () => void, setError: (message: string) => void, fallback: string) {
  if (error instanceof ApiError && error.status === 401) {
    onUnauthorized();
    return;
  }
  setError(error instanceof Error ? error.message : fallback);
}

/**
 * Catalog CMS.
 *
 * Always embedded by CatalogPage under GroupPage, which owns the page's h1,
 * padding and scroll shell. This page renders the section heading, the search
 * and the category cards; the primitives own every control and surface.
 */
const Cms: FC<CmsProps> = ({ onUnauthorized, onPreview }) => {
  const { username } = useContext(AuthContext);
  // Drafts are scoped to the signed-in account, so one operator never restores
  // another's unsaved text.
  const draftScope = username ?? 'unknown';
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  /** The name being typed for a new category, or null when the form is closed. */
  const [newCategory, setNewCategory] = useState<string | null>(() => readDraft(newCategoryDraftKey(draftScope), isNewCategoryDraft));
  const [expansion, setExpansion] = useState<Record<number, boolean>>({});
  const [editing, setEditing] = useState<EditTarget>(null);
  /** The category the preview rail follows when nothing is being edited. */
  const [focusedCategoryId, setFocusedCategoryId] = useState<number | null>(null);
  /** The category currently being dragged, or null when no drag is in flight. */
  const [draggingId, setDraggingId] = useState<number | null>(null);
  /** Mirror of `draggingId` for the drop handler, which may run before a re-render. */
  const draggingIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetchAdminCatalog()
      .then((result) => {
        if (cancelled) return;
        setCategories(result);
        setError('');
      })
      .catch((err: unknown) => {
        if (!cancelled) report(err, onUnauthorized, setError, 'Could not load the catalog.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [onUnauthorized]);

  /** Re-read the server's copy after a failed optimistic update. */
  const reloadCatalog = useCallback(() => {
    fetchAdminCatalog()
      .then((result) => {
        setCategories(result);
        setError('');
      })
      .catch((err: unknown) => report(err, onUnauthorized, setError, 'Could not load the catalog.'));
  }, [onUnauthorized]);

  /**
   * Another session may have changed the catalogue while this tab sat in the
   * background, so coming back re-reads it. It stands down while a write is in
   * flight (that row is about to change anyway) and while any draft is stored:
   * unsaved work beats freshness, and the chip already says the draft exists.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (savesInFlight() || hasStoredDrafts(draftScope)) return;
      reloadCatalog();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [draftScope, reloadCatalog]);

  /**
   * Keep the rail useful when nothing is open: show the focused category, or the
   * first one. While a category's fields are open its editor publishes the live
   * palette instead, so its own effect owns the rail.
   */
  useEffect(() => {
    if (editing?.kind === 'category') return;
    const shown = (focusedCategoryId === null ? undefined : categories.find((entry) => entry.id === focusedCategoryId)) ?? categories[0];
    onPreview?.(shown ? categoryPreview(shown) : null);
  }, [categories, editing, focusedCategoryId, onPreview]);

  const replaceCategory = useCallback((updated: AdminCategory) => {
    setCategories((current) => current.map((entry) => (entry.id === updated.id ? updated : entry)));
  }, []);

  const removeCategory = useCallback((categoryId: number) => {
    setCategories((current) => current.filter((entry) => entry.id !== categoryId));
  }, []);

  const addItem = useCallback((categoryId: number, item: AdminItem) => {
    setCategories((current) => current.map((entry) => (entry.id === categoryId ? { ...entry, items: [...entry.items, item] } : entry)));
  }, []);

  const removeItem = useCallback((categoryId: number, itemId: number) => {
    setCategories((current) =>
      current.map((entry) => (entry.id === categoryId ? { ...entry, items: entry.items.filter((item) => item.id !== itemId) } : entry))
    );
  }, []);

  const isExpanded = useCallback((categoryId: number) => expansion[categoryId] ?? STARTS_EXPANDED, [expansion]);

  const toggleCollapsed = useCallback((categoryId: number) => {
    // Opening a category also points the preview at it.
    setFocusedCategoryId(categoryId);
    setExpansion((current) => ({ ...current, [categoryId]: !(current[categoryId] ?? STARTS_EXPANDED) }));
  }, []);

  const hasNewCategoryDraft = newCategoryDraftDirty(newCategory);

  // A half-typed category name is a draft too: the same sessionStorage contract
  // as the editor drafts, and the same dirty-only close guard. Creating or
  // cancelling empties it, which clears the key.
  useEffect(() => {
    const key = newCategoryDraftKey(draftScope);
    if (hasNewCategoryDraft) writeDraft(key, newCategory);
    else clearDraft(key);
  }, [newCategory, hasNewCategoryDraft, draftScope]);

  useEffect(() => warnBeforeUnload(hasNewCategoryDraft), [hasNewCategoryDraft]);

  const addCategory = () => {
    const name = (newCategory ?? '').trim();
    if (!name) return;

    trackSave(() => createCategory(name))
      .then((created) => {
        setCategories((current) => [...current, created]);
        setExpansion((current) => ({ ...current, [created.id]: true }));
        setNewCategory(null);
        setNotice('Category created.');
      })
      .catch((err: unknown) => report(err, onUnauthorized, setError, 'Could not create the category.'));
  };

  /**
   * Persist a reordered list.
   *
   * The list is shown immediately and only replaced once the server confirms,
   * so dragging feels instant. If the save fails the local guess may now
   * disagree with the stored order, so the server's copy is re-read.
   */
  const saveOrder = useCallback(
    (ordered: AdminCategory[]) => {
      trackSave(() => reorderCategories(ordered.map((category) => category.id)))
        .then((saved) => {
          setCategories(saved);
          setError('');
        })
        .catch((err: unknown) => {
          report(err, onUnauthorized, setError, 'Could not save the category order.');
          reloadCatalog();
        });
    },
    [onUnauthorized, reloadCatalog]
  );

  /** Move a category one slot earlier (-1) or later (+1). */
  const moveCategory = useCallback(
    (categoryId: number, delta: -1 | 1) => {
      const index = categories.findIndex((entry) => entry.id === categoryId);
      const target = index + delta;
      if (index < 0 || target < 0 || target >= categories.length) return;

      const next = [...categories];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      setCategories(next);
      saveOrder(next);
    },
    [categories, saveOrder]
  );

  const beginDrag = useCallback((categoryId: number) => {
    draggingIdRef.current = categoryId;
    setDraggingId(categoryId);
  }, []);

  const endDrag = useCallback(() => {
    draggingIdRef.current = null;
    setDraggingId(null);
  }, []);

  /** Drop the dragged category into the slot of the one it was released over. */
  const dropCategory = useCallback(
    (targetId: number) => {
      // Read the ref rather than state: drop can arrive before React re-renders
      // with the category that dragstart just recorded.
      const sourceId = draggingIdRef.current;
      endDrag();
      if (sourceId === null || sourceId === targetId) return;

      const from = categories.findIndex((entry) => entry.id === sourceId);
      const to = categories.findIndex((entry) => entry.id === targetId);
      if (from < 0 || to < 0) return;

      const next = [...categories];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      setCategories(next);
      saveOrder(next);
    },
    [categories, endDrag, saveOrder]
  );

  /**
   * What the search leaves of the catalogue.
   *
   * A category is kept when its own name matches, and then shows every item;
   * otherwise it is kept only for the items that match. Grouping survives, so a
   * hit is always shown in the category it belongs to.
   */
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return categories.map((category) => ({ category, items: category.items }));
    }
    return categories
      .map((category) => {
        const nameMatches = category.name.toLowerCase().includes(needle);
        return {
          category,
          nameMatches,
          items: nameMatches ? category.items : category.items.filter((item) => item.title.toLowerCase().includes(needle)),
        };
      })
      .filter((entry) => entry.nameMatches || entry.items.length > 0)
      .map(({ category, items }) => ({ category, items }));
  }, [categories, query]);

  const searching = query.trim().length > 0;

  return (
    <div className="cms">
      <SectionHeader title="Items" description={loading ? 'Loading the catalogue…' : summaryLine(categories)} />

      {error && (
        <Notice variant="error" onDismiss={() => setError('')}>
          {error}
        </Notice>
      )}

      {notice && !error && (
        <Notice variant="success" className="cmsNotice">
          {notice}
        </Notice>
      )}

      <Stack direction="row" gap={3} className="cmsToolbar">
        <div className="cmsSearch">
          <Input
            type="search"
            placeholder="Search categories or items…"
            aria-label="Search categories or items"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        {newCategory === null && (
          <Button variant="primary" onClick={() => setNewCategory('')}>
            + New category
          </Button>
        )}
      </Stack>

      {newCategory !== null && (
        <Card className="cmsNewCategory">
          <Stack gap={4}>
            <CardTitle level={3}>New category</CardTitle>
            <Field label="New category name">
              <Input
                value={newCategory}
                autoFocus
                placeholder="Stickers (Die Cut)"
                onChange={(event) => setNewCategory(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') addCategory();
                  if (event.key === 'Escape') setNewCategory(null);
                }}
              />
            </Field>
            <Meta>You can set its colours afterwards, from the category’s ⋯ menu.</Meta>
            <Stack direction="row" gap={3}>
              <Button variant="primary" disabled={!newCategory.trim()} onClick={addCategory}>
                Create category
              </Button>
              <Button variant="secondary" onClick={() => setNewCategory(null)}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      {loading ? (
        <Meta role="status">Loading the catalogue…</Meta>
      ) : categories.length === 0 ? (
        <EmptyState
          title="No categories yet."
          description="Create your first category to start building the catalog."
          action={
            <Button variant="primary" onClick={() => setNewCategory('')}>
              + New category
            </Button>
          }
        />
      ) : visible.length === 0 ? (
        <Meta>No matching categories or items.</Meta>
      ) : (
        visible.map(({ category, items }) => (
          <CategoryCard
            key={category.id}
            category={category}
            visibleItems={items}
            position={categories.findIndex((entry) => entry.id === category.id) + 1}
            total={categories.length}
            dragging={draggingId === category.id}
            collapsed={searching ? false : !isExpanded(category.id)}
            editingCategory={editing?.kind === 'category' && editing.categoryId === category.id}
            editingItemId={editing?.kind === 'item' ? editing.itemId : null}
            addingItem={editing?.kind === 'newItem' && editing.categoryId === category.id}
            onToggleCollapsed={() => toggleCollapsed(category.id)}
            onEditCategory={(id) => setEditing(id === null ? null : { kind: 'category', categoryId: id })}
            onEditItem={(id) => setEditing(id === null ? null : { kind: 'item', itemId: id })}
            onAddingItem={(id) => setEditing(id === null ? null : { kind: 'newItem', categoryId: id })}
            onMove={moveCategory}
            onDragStart={beginDrag}
            onDragEnd={endDrag}
            onDrop={dropCategory}
            draftScope={draftScope}
            onCategoryChanged={replaceCategory}
            onCategoryRemoved={removeCategory}
            onItemAdded={addItem}
            onItemRemoved={removeItem}
            onNotice={setNotice}
            onError={setError}
            onUnauthorized={onUnauthorized}
            onPreview={onPreview}
          />
        ))
      )}
    </div>
  );
};

export default Cms;
